// The offline conversation partner — a port of `tutor/TutorEngine.kt`.
//
// One engine instance drives one conversation: it remembers which questions it
// has already asked and how far through a scenario script it is. All of it runs
// in the page, which is what replaces the `/chat` endpoint of the original
// React Native version.
import { detectTopic, GENERAL_TOPIC, reflect, type Topic } from './conversation';
import { check, type Correction } from './grammarChecker';
import { mod } from './mod';
import { freeTalk, type Scenario } from './scenarios';
import { idiomTip, upgradeTip } from './vocabulary';

/** Everything the tutor wants to say in response to one learner message. */
export interface TutorTurn {
  /** The conversational part — reaction plus a follow-up question. */
  text: string;
  /** Praise shown above the reply when the sentence was already correct. */
  praise: string | null;
  /**
   * Grammar fixes to show, already de-duplicated and capped. Empty when the
   * learner has switched correction feedback off.
   */
  corrections: Correction[];
  /** The learner's sentence, rewritten. Null when nothing needed changing. */
  correctedSentence: string | null;
  /** An occasional vocabulary or idiom nudge. */
  tip: string | null;
  /**
   * Everything the checker found, shown or not — the Progress screen still
   * counts mistakes while corrections are switched off.
   */
  detected: Correction[];
}

const MAX_CORRECTIONS = 3;

const PRAISE: readonly string[] = [
  'That was perfect English — no corrections from me! 🌟',
  'Really well said. Your sentence structure was spot on. 👏',
  'Nice one — that sounded completely natural. ✨',
  'Great job, everything was correct there. 🎉',
  'Excellent — clear and accurate. Keep it up! 💪',
];

const FAREWELLS: ReadonlySet<string> = new Set([
  'bye', 'goodbye', 'good bye', 'see you', 'see you later', 'bye bye',
  'good night', 'goodnight', 'talk to you later', 'i have to go',
]);

const STUCK: ReadonlySet<string> = new Set([
  "i don't know", 'i dont know', 'idk', 'help', "i'm stuck", 'im stuck',
  'no idea', "i can't", 'i cant', 'hint',
]);

/** Kotlin's `trimEnd('.', '!', '?')`. */
function trimEndPunctuation(text: string): string {
  let end = text.length;
  while (end > 0 && '.!?'.includes(text.charAt(end - 1))) end--;
  return text.slice(0, end);
}

export class TutorEngine {
  readonly scenario: Scenario;

  private turnCount = 0;
  private scriptIndex = 0;
  private readonly askedQuestions = new Set<string>();

  constructor(scenario: Scenario = freeTalk) {
    this.scenario = scenario;
  }

  /** The tutor's first line, shown before the learner says anything. */
  opener(): string {
    return this.scenario.opener;
  }

  /** True once a scripted role-play has run out of prompts. */
  get scriptFinished(): boolean {
    return this.scenario.script.length === 0 || this.scriptIndex >= this.scenario.script.length;
  }

  /**
   * Answers one learner message. With `showCorrections` off the tutor keeps the
   * conversation going without ever interrupting to fix grammar — mistakes are
   * still detected for the Progress screen, they just aren't shown.
   */
  respond(userText: string, showCorrections = true): TutorTurn {
    this.turnCount++;
    const trimmed = userText.trim();
    const result = check(trimmed);
    const corrections = dedupe(result.corrections).slice(0, MAX_CORRECTIONS);
    const words = trimmed.split(/\s+/).filter((w) => w.trim() !== '');
    const topic = detectTopic(trimmed);
    const seed = this.turnCount * 7 + trimmed.length;

    let body: string;
    if (isFarewell(trimmed)) {
      body = this.farewell();
    } else if (isStuck(trimmed)) {
      body = this.hint();
    } else if (!this.scriptFinished) {
      body = this.scriptedReply(trimmed, topic, seed);
    } else {
      body = this.freeReply(trimmed, topic, words.length, seed);
    }

    let praise: string | null = null;
    if (corrections.length === 0 && words.length >= 6) {
      praise = PRAISE[mod(seed, PRAISE.length)]!;
    } else if (corrections.length === 0 && words.length >= 3 && words.length <= 5) {
      praise = 'Correct — nicely put! 👏';
    }

    const tip =
      this.turnCount % 3 === 0
        ? idiomTip(trimmed, seed) ?? upgradeTip(trimmed, seed)
        : null;

    return {
      text: body,
      praise,
      corrections: showCorrections ? corrections : [],
      correctedSentence:
        !showCorrections || corrections.length === 0 ? null : result.corrected,
      tip,
      detected: corrections,
    };
  }

  /** Useful phrases for the current scenario, for the "I'm stuck" button. */
  hint(): string {
    const phrases = this.scenario.usefulPhrases;
    if (phrases.length === 0) {
      return "No pressure — just answer in one simple sentence. I'll help you polish it.";
    }
    const phrase = phrases[mod(this.turnCount, phrases.length)]!;
    return `No problem — try something like: "${phrase}" Give it a go in your own words.`;
  }

  reset(): void {
    this.turnCount = 0;
    this.scriptIndex = 0;
    this.askedQuestions.clear();
  }

  // --------------------------------------------------------------- reply parts

  private scriptedReply(userText: string, topic: Topic, seed: number): string {
    const next = this.scenario.script[this.scriptIndex]!;
    this.scriptIndex++;
    const ack =
      userText.length > 25 && seed % 2 === 0
        ? `${topic.reactions[mod(seed, topic.reactions.length)]!} `
        : '';
    return ack + next;
  }

  private freeReply(userText: string, topic: Topic, wordCount: number, seed: number): string {
    if (wordCount <= 2) {
      return (
        "Try giving me a full sentence — it's the fastest way to improve. " +
        'For example, instead of just a word or two, tell me why. ' +
        this.pickQuestion(topic, seed)
      );
    }
    const reaction = topic.reactions[mod(seed, topic.reactions.length)]!;
    const answer = userText.trimEnd().endsWith('?') ? answerQuestion(userText) : null;
    const echo = echoOf(userText, seed);
    return [answer ?? reaction, echo, this.pickQuestion(topic, seed)]
      .filter((part): part is string => part !== null)
      .join(' ');
  }

  private pickQuestion(topic: Topic, seed: number): string {
    let pool = topic.questions.filter((q) => !this.askedQuestions.has(q));
    if (pool.length === 0) {
      pool = GENERAL_TOPIC.questions.filter((q) => !this.askedQuestions.has(q));
    }
    if (pool.length === 0) {
      pool = [...topic.questions];
    }
    const question = pool[mod(seed, pool.length)]!;
    this.askedQuestions.add(question);
    return question;
  }

  private farewell(): string {
    return (
      `It was great practising with you today! You spoke ${this.turnCount} times — that's real progress. ` +
      "Come back soon and we'll pick up where we left off. 👋"
    );
  }
}

/** Occasionally mirrors the learner's own words back, the way a tutor would. */
function echoOf(userText: string, seed: number): string | null {
  if (seed % 3 !== 0) return null;
  const clause = userText
    .split(/[.!?,]/)
    .map((c) => c.trim())
    .find((c) => {
      const size = c.split(' ').length;
      return size >= 3 && size <= 9;
    });
  if (clause === undefined) return null;
  return `So ${reflect(clause).toLowerCase()} — got it.`;
}

function answerQuestion(userText: string): string {
  const lower = userText.toLowerCase();
  if (lower.includes('how are you') || lower.includes('and you')) {
    return "I'm doing well, thanks for asking — and I'm always ready to practise!";
  }
  if (lower.startsWith('what is') || lower.startsWith("what's") || lower.startsWith('what does')) {
    return "Good question. Try saying what you think it means first — then I'll tell you if you got it.";
  }
  if (lower.includes('can you') || lower.includes('do you')) {
    return "I'm a practice partner, so my job is mostly to listen and correct. But I'm curious —";
  }
  return "That's a great question to ask in English.";
}

function isFarewell(text: string): boolean {
  return FAREWELLS.has(trimEndPunctuation(text.toLowerCase().trim()));
}

function isStuck(text: string): boolean {
  return STUCK.has(trimEndPunctuation(text.toLowerCase().trim()));
}

/** Keeps one correction per rule so the learner isn't flooded with the same note. */
function dedupe(corrections: Correction[]): Correction[] {
  const seen = new Set<string>();
  return corrections.filter((c) => {
    const key = `${c.rule}|${c.original.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
